import React from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Link from "@mui/joy/Link";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import { AnalyticsFilters, AnalyticsFiltersState } from "../../../bids/analytics/components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../../bids/analytics/hooks";
import { formatInt, formatUtcLocalShort, toNum } from "../../../bids/analytics/format";
import { Game } from "../../../../models/games";
import { OrderTimingReport, OrderTimingStats, StuckOrders } from "../../../../models/orderAnalytics";
import { ordersTimingApi } from "../ordersAnalyticsApi";
import { orderUrl } from "../failDetails";
import {
    formatAgeHours,
    formatSeconds,
    stuckStatusLabel,
    orderTypeLabel,
    stageLabel,
    stuckStatusUrl,
    timingSlices,
} from "../timing";
import { HowTo } from "../Shared";
import { ListContext } from "./FailReasonsCharts";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function TimingTab({ isActive, filters, onFiltersChange, games, gamesLoading }: Props) {
    const [state, refresh] = useAsyncBlock<OrderTimingReport>(
        () =>
            ordersTimingApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
                stuckHours: filters.stuckHours,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId, filters.stuckHours]
    );
    const report = state.data;
    const isEmpty = report != null
        && report.stages.every((s) => toNum(s.count) === 0) && toNum(report.stuck.total) === 0;
    const ctx: ListContext = { gameId: filters.gameId, from: filters.from, to: filters.to };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showStuckHours
            />
            {state.loading && !report && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {isEmpty && <EmptyState />}
            {report && !isEmpty && <TimingContent report={report} ctx={ctx} stuckHours={filters.stuckHours} />}
        </Box>
    );
}

const STAGES_HOW_TO_READ = (
    <HowTo items={[
        ["Этапы", "создан → запрос продавцу → бот получил предметы → отправлено покупателю → покупатель принял (завершён)"],
        ["Медиана", "половина ордеров прошла этап быстрее этого времени — типичная скорость"],
        ["p90", "90% ордеров прошли этап быстрее, 10% — медленнее; показывает «долгий хвост»"],
        ["Среднее", "сильно растёт от нескольких очень долгих ордеров, поэтому может быть больше p90"],
        ["Точность", "до часа — до секунды («< 1 сек», «35 сек»), дальше — до минуты"],
        ["Ордеров на этапе", "разное: первые три этапа считаются по всем ордерам с отметками этапа, включая неуспешные; последний этап и «Всего» — только по завершённым. У P2P отметок этапов нет — только «Всего»"],
        ["Запрос продавцу", "последний запрос: если офер отправляли повторно, первый этап включает ожидание до повтора"],
        ["Срез", "выберите тип сделки или игру, чтобы увидеть ту же таблицу только по ним"],
    ]} />
);

const STAGES_WATCH_FOR = (
    <WatchSignals items={[
        {
            signal: "Медиана «Запрос продавцу → получено ботом»",
            growsIsGood: false,
            ifGrows: "продавцы дольше принимают офер — растёт риск таймаута по вине продавца.",
            ifFalls: "продавцы передают предметы быстрее.",
        },
        {
            signal: "p90 «Отправлено покупателю → завершён»",
            growsIsGood: false,
            ifGrows: "покупатели всё дольше принимают предметы — деньги и предметы дольше «в пути».",
            ifFalls: "покупатели забирают предметы быстрее.",
        },
        {
            signal: "Медиана «Получено ботом → отправлено покупателю»",
            growsIsGood: false,
            ifGrows: "задержка на нашей стороне — боты дольше отправляют предметы покупателю.",
            ifFalls: "боты отправляют предметы сразу.",
        },
    ]} />
);

function TimingContent({ report, ctx, stuckHours }: { report: OrderTimingReport; ctx: ListContext; stuckHours: number }) {
    const slices = timingSlices(report);
    const [sliceKey, setSliceKey] = React.useState("all");
    const slice = slices.find((s) => s.key === sliceKey) ?? slices[0];

    return (
        <>
            <AnalyticsCard title="Этапы" subtitle="Сколько времени ордера проходят каждый этап"
                           howToRead={STAGES_HOW_TO_READ} watchFor={STAGES_WATCH_FOR} snapshotLabel="period">
                <FormControl size="sm" sx={{ maxWidth: 360 }}>
                    <FormLabel>Срез</FormLabel>
                    <Select size="sm" value={slice.key} onChange={(_, v) => setSliceKey((v as string) ?? "all")}>
                        {slices.map((s) => <Option key={s.key} value={s.key}>{s.label}</Option>)}
                    </Select>
                </FormControl>
                <StagesTable stages={slice.stages} />
            </AnalyticsCard>

            <AnalyticsCard
                title="Зависшие"
                subtitle={`Не завершённые ордера периода старше ${formatInt(report.stuck.stuckHours)} ч`}
                howToRead={
                    <HowTo items={[
                        ["Зависшие", "ордера выбранного периода, которые ещё не завершены, не отменены и не возвращены, и созданы раньше порога «Зависла старше (ч)»"],
                        ["Счётчик статуса", "ссылка на список этих ордеров в новом окне"],
                        ["Таблица", "до 50 самых старых; номер — карточка ордера в новом окне"],
                    ]} />
                }
                watchFor={
                    <WatchSignals items={[{
                        signal: "Число зависших",
                        growsIsGood: false,
                        ifGrows: "ордера застревают — разберите самые старые и статус, в котором их больше всего.",
                        ifFalls: "ордера доходят до финала вовремя.",
                    }]} />
                }
                snapshotLabel="now"
            >
                <StuckBlock stuck={report.stuck} ctx={ctx} stuckHours={stuckHours} />
            </AnalyticsCard>
        </>
    );
}

function StagesTable({ stages }: { stages: OrderTimingStats[] }) {
    if (stages.every((s) => toNum(s.count) === 0)) {
        return <Typography level="body-sm" color="neutral">Нет ордеров с отметками этапов.</Typography>;
    }
    return (
        <ScrollTable>
            <Table size="sm" sx={{ minWidth: 720 }}>
                <thead>
                    <tr>
                        <th scope="col" style={{ width: "40%" }}>Этап</th>
                        <th scope="col">Ордеров</th>
                        <th scope="col">Медиана</th>
                        <th scope="col">p90</th>
                        <th scope="col">Среднее</th>
                    </tr>
                </thead>
                <tbody>
                    {stages.map((s) => (
                        <tr key={s.stage}>
                            <th scope="row" style={{ fontWeight: 400 }}>{stageLabel(s.stage)}</th>
                            <td>{formatInt(s.count)}</td>
                            <td>{formatSeconds(s.medianSeconds)}</td>
                            <td>{formatSeconds(s.p90Seconds)}</td>
                            <td>{formatSeconds(s.avgSeconds)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ScrollTable>
    );
}

function StuckBlock({ stuck, ctx, stuckHours }: { stuck: StuckOrders; ctx: ListContext; stuckHours: number }) {
    if (toNum(stuck.total) === 0) {
        return <Typography level="body-sm" color="neutral">Зависших нет.</Typography>;
    }
    const isTruncated = stuck.oldest.length < toNum(stuck.total);
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                <MetricHint label="Зависших" color="warning" value={formatInt(stuck.total)} />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {stuck.byStatus.map((s) => (
                        <Chip key={`${s.status}-${s.isManualStart}`} variant="soft" color="warning">
                            <Link component={RouterLink} target="_blank" rel="noopener noreferrer"
                                  to={stuckStatusUrl(s.status, ctx, stuckHours)} color="neutral" overlay underline="none">
                                {stuckStatusLabel(s.status, s.isManualStart)}: {formatInt(s.count)}
                            </Link>
                        </Chip>
                    ))}
                </Box>
            </Box>
            {isTruncated && (
                <Typography level="body-xs" color="neutral">
                    Показаны {formatInt(stuck.oldest.length)} самых старых из {formatInt(stuck.total)}
                </Typography>
            )}
            <ScrollTable>
                <Table size="sm" sx={{ minWidth: 820 }}>
                    <thead>
                        <tr>
                            <th scope="col" style={{ width: 120 }}>Ордер</th>
                            <th scope="col">Игра</th>
                            <th scope="col">Статус</th>
                            <th scope="col">Тип</th>
                            <th scope="col">Создан</th>
                            <th scope="col" style={{ width: 100 }}>Возраст</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stuck.oldest.map((o) => (
                            <tr key={o.orderId}>
                                <td>
                                    <Link component={RouterLink} target="_blank" rel="noopener noreferrer"
                                          to={orderUrl(o.orderId)}>#{o.orderId}</Link>
                                </td>
                                <td>{o.gameName}</td>
                                <td>{stuckStatusLabel(o.status, o.isManualStart)}</td>
                                <td>{orderTypeLabel(o.type)}</td>
                                <td>{formatUtcLocalShort(o.createdAt)}</td>
                                <td>{formatAgeHours(o.ageHours)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </ScrollTable>
        </Box>
    );
}
